/* 工程看板（/board）筛选缓存：读写 + 一次性「返回令牌」
 *
 * 需求：
 *  - 切换页面（离开看板去别的模块）后回来 → 筛选清空；
 *  - 看板 → 点列表行进项目详情 → 返回 → 保留主搜索条件。
 *
 * 做法：把判据从「localStorage 里有没有值」换成「这次挂载是不是从详情页回来的」。
 *  - 去详情前 markBoardReturn() 埋下一次性令牌；
 *  - 看板挂载时 isBoardReturn() 判定：命中 → 恢复筛选；未命中 → 清空缓存并回默认；
 *  - 令牌读一次即消费（consumeBoardReturn），只对紧接的那一次返回生效。
 */

const RETURN_TOKEN = "tb_keep_filters";

/* 参与缓存 / 恢复的筛选器 —— 与工具栏一致：
 * 团队、阶段、状态、分公司、销售、搜索框，外加 URL 驱动的「AI 风险」。
 * 注：「业主语言」筛选已于 2026-10-14 按需求从看板工具栏移除，故不再参与缓存。 */
export const BOARD_FILTER_KEYS = {
  query: "tb_query",
  phase: "tb_phaseFilter",
  status: "tb_statusFilter",
  branch: "tb_branchFilter",
  sales: "tb_salesFilter",
  risk: "tb_riskFilter",
};
export const boardTeamKey = (user) => `tb_teamFilter:${user?.username || ""}`;

/** 多选筛选统一为字符串数组，空数组表示全部。
 * 旧版缓存为单个值或 all，新版为 JSON 数组；兼容旧缓存，避免从详情返回后丢失单选条件。
 * allowedValues 限定当前有效选项，去除重复及已失效值，防止缓存导致列表被隐性筛空。
 */
export const normalizeBoardSelection = (value, allowedValues) => {
  let values = value;
  if (typeof values === "string") {
    try { values = JSON.parse(values); } catch { /* 旧版团队/状态缓存是未编码的字符串。 */ }
  }
  if (!Array.isArray(values)) values = values == null || values === "all" ? [] : [values];
  return [...new Set(values.map(String))].filter((item) => allowedValues.includes(item));
};

/* 去项目详情前调用：埋令牌，并记录目标路径（供 Layout 全局守卫校验） */
export const markBoardReturn = (path) => {
  try { sessionStorage.setItem(RETURN_TOKEN, path || "1"); } catch { /* 隐私模式等，忽略 */ }
};

/* 看板挂载时调用：本次是否为「从详情返回」 */
export const isBoardReturn = () => {
  try { return sessionStorage.getItem(RETURN_TOKEN) !== null; } catch { return false; }
};

/* 消费令牌：挂载 effect 里调用一次，保证只对紧接的那一次返回生效 */
export const consumeBoardReturn = () => {
  try { sessionStorage.removeItem(RETURN_TOKEN); } catch { /* ignore */ }
};

/* 回读单个筛选器：keep=false 时直接返回 fallback（即「不缓存」） */
export const restoreBoardFilter = (name, user, fallback, keep) => {
  if (!keep) return fallback;
  try {
    const key = name === "team" ? boardTeamKey(user) : BOARD_FILTER_KEYS[name];
    return localStorage.getItem(key) || fallback;
  } catch { return fallback; }
};

/* 清空看板全部筛选缓存 */
export const clearBoardFilterStorage = (user) => {
  try {
    Object.values(BOARD_FILTER_KEYS).forEach((k) => localStorage.removeItem(k));
    localStorage.removeItem(boardTeamKey(user));
  } catch { /* ignore */ }
};

/* Layout 全局守卫：令牌目标路径与当前路径不符、且不是回到看板 → 立即作废令牌。
 * 堵「看板 → 详情 → 没返回，而是拐去别的页 → 再回看板」被误判为「从详情返回」。 */
export const guardBoardReturn = (pathname) => {
  try {
    const dest = sessionStorage.getItem(RETURN_TOKEN);
    if (dest && dest !== pathname && pathname !== "/board") sessionStorage.removeItem(RETURN_TOKEN);
  } catch { /* ignore */ }
};
